import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { isNative } from '../utils/native';
import { persistNativeCookies } from '../utils/nativeCookies';
import { t } from '../i18n';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSessionUser, setIsSessionUser] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [loginPromptMessage, setLoginPromptMessage] = useState('');
    const [loginPromptSignup, setLoginPromptSignup] = useState(false);
    const [pendingSocialAuth, setPendingSocialAuth] = useState(null);

    const checkAuth = useCallback(async () => {
        try {
            const response = await api.checkAuthStatus();
            if (response.authenticated) {
                setUser(response.user);
                setIsAuthenticated(true);
                setIsSessionUser(false);
            } else {
                setUser(null);
                setIsAuthenticated(false);
                setIsSessionUser(response.session_user || false);
            }
        } catch (error) {
            console.error('Error checking auth status:', error);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const doCheck = async () => {
            try {
                const response = await api.checkAuthStatus();
                if (!mounted) return;
                if (response.authenticated) {
                    setUser(response.user);
                    setIsAuthenticated(true);
                    setIsSessionUser(false);
                } else {
                    setUser(null);
                    setIsAuthenticated(false);
                    setIsSessionUser(response.session_user || false);
                }
            } catch (error) {
                if (!mounted) return;
                console.error('Error checking auth status:', error);
                setUser(null);
                setIsAuthenticated(false);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        doCheck();
        return () => { mounted = false; };
    }, []);

    // A sign-in or sign-out rotates the session cookie; make sure Android keeps the new one.
    const userId = user ? user.id : null;
    useEffect(() => {
        if (!loading) persistNativeCookies();
    }, [userId, loading]);

    // Listen for session expiry events from API layer
    useEffect(() => {
        const handleSessionExpired = () => {
            checkAuth();
        };
        window.addEventListener('wl:session-expired', handleSessionExpired);
        return () => window.removeEventListener('wl:session-expired', handleSessionExpired);
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await api.login(email, password);
            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
                setIsSessionUser(false);
                setShowLoginPrompt(false);
                return { success: true };
            }
            return {
                success: false,
                error: response.error,
                email_unverified: response.email_unverified,
                email: response.email,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const signup = async (email, password, username = '') => {
        try {
            const response = await api.signup(email, password, username);
            if (response.success) {
                // 'Account Created' is now tracked server-side (api_signup).
                if (response.verification_required) {
                    return {
                        success: true,
                        verification_required: true,
                        email: response.email,
                        message: response.message,
                    };
                }
                setUser(response.user);
                setIsAuthenticated(true);
                setIsSessionUser(false);
                setShowLoginPrompt(false);
                return { success: true };
            }
            return {
                success: false,
                error: response.error,
                can_resend: response.can_resend,
                email: response.email,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const logout = async () => {
        try {
            await api.logout();
            setUser(null);
            setIsAuthenticated(false);
            setIsSessionUser(false);
            if (!isNative()) {
                // Web: refresh to get a new anonymous session
                window.location.reload();
            }
            // Mobile: MobileAuthGate will show login screen automatically
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    // signup: open on the create-account form (prompts that ask a guest to sign up).
    const promptLogin = (message = '', { signup = false } = {}) => {
        setLoginPromptMessage(message);
        setLoginPromptSignup(signup);
        setShowLoginPrompt(true);
    };

    const dismissLoginPrompt = () => {
        setShowLoginPrompt(false);
        setLoginPromptMessage('');
        setLoginPromptSignup(false);
    };

    const socialLogin = async (provider, accessToken, idToken = null, username = null) => {
        try {
            const payload = {
                provider,
                accessToken,
            };

            if (idToken) {
                payload.idToken = idToken;
            }

            if (username) {
                payload.username = username;
            }

            const response = await api.socialLogin(payload);

            if (response.success) {
                setUser(response.user);
                setIsAuthenticated(true);
                setIsSessionUser(false);
                setShowLoginPrompt(false);
                setPendingSocialAuth(null);
                return { success: true };
            }

            // Check if username is needed (new social account)
            if (response.needsUsername) {
                // 'Account Created' is now tracked server-side (api_social_auth).
                setPendingSocialAuth({
                    provider,
                    accessToken,
                    idToken,
                    email: response.email,
                    name: response.name,
                });
                return {
                    success: false,
                    needsUsername: true,
                    email: response.email,
                    name: response.name,
                };
            }

            return { success: false, error: response.error };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const completeSocialAuthWithUsername = async (username) => {
        if (!pendingSocialAuth) {
            return { success: false, error: t('auth.errors.noPendingSignIn') };
        }

        const result = await socialLogin(
            pendingSocialAuth.provider,
            pendingSocialAuth.accessToken,
            pendingSocialAuth.idToken,
            username
        );

        if (result.success) {
            setPendingSocialAuth(null);
        }

        return result;
    };

    const value = {
        user,
        isAuthenticated,
        isSessionUser,
        loading,
        showLoginPrompt,
        loginPromptMessage,
        loginPromptSignup,
        pendingSocialAuth,
        login,
        signup,
        logout,
        checkAuth,
        promptLogin,
        dismissLoginPrompt,
        setShowLoginPrompt,
        setLoginPromptMessage,
        socialLogin,
        completeSocialAuthWithUsername,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthContext;
